import React from 'react';
import {
  ActivitiesContextProvider,
  ActivityInterface,
  useActivitiesContext,
} from 'pages/Activities/activities.context';
import { UsersContextProvider, useUsersContext } from 'context/users.context';
import Wizard, { WizardStepInterface } from 'components/Wizard/Wizard';
import {
  ActivityWizardContextProvider,
  useActivityWizardContext,
} from 'pages/Activities/ActivityWizard/activityWizard.context';
import ActivityDetails from 'pages/Activities/ActivityWizard/ActivityDetails/ActivityDetails';
import StepDetails from 'pages/Activities/ActivityWizard/StepDetails/StepDetails';
import Overview from 'components/Overview/Overview';
import { useNavigate, useParams } from 'react-router-dom';
import { GET_ACTIVITY } from 'graphql/activities/queries/getActivity.gql';
import { useQuery } from '@apollo/client';
import Loading from 'components/Messages/Loading/Loading';
import Error from 'components/Messages/Error/Error';
import { CampaignsContextProvider, useCampaignsContext } from 'pages/Campaigns/campaigns.context';
import { StoresContextProvider, useStoresContext } from 'pages/Stores/stores.context';
import { getUsersName } from 'utils/formatting';
import { createUpdateActivityFromActivity } from 'pages/Activities/utils';
import { hasPermissions } from 'config/ability';
import { useAuthContext } from 'context/auth.context';
import { MediaGalleryPageContextProvider } from 'pages/MediaGalleryPage/media-gallery-page.context';
import { activityValidator } from 'pages/Activities/ActivityWizard/utils/activityValidator';

const UpdateActivityWizardComponent = () => {
  const {
    handleSaveActivity,
    newActivity,
    setNewActivity,
    setExistingActivity,
    loading: wizardLoading,
  } = useActivityWizardContext();
  const { activityId } = useParams();
  const navigate = useNavigate();
  const { ability } = useAuthContext();
  const { saveActivityLoading } = useActivitiesContext();

  const { getStoreById } = useStoresContext();
  const storeNames = newActivity.assignedStoreIds?.map((id) => getStoreById(id)?.name ?? 'N/A');

  const { getCampaignById } = useCampaignsContext();
  const campaign = newActivity.campaignId ? getCampaignById(newActivity.campaignId) : undefined;

  const { getUserById } = useUsersContext();
  const assignedUserNames = newActivity.assignedUserIds.map(
    (id) => getUsersName(getUserById(id)) ?? ''
  );

  // fetch activity
  const { data, loading, error } = useQuery(GET_ACTIVITY, {
    skip: !hasPermissions(ability, 'update', 'Activity'),
    variables: {
      id: Number(activityId),
    },
    onCompleted: async ({ activity }: { activity: ActivityInterface }) => {
      await setExistingActivity(activity);
      const newActivity = createUpdateActivityFromActivity(activity);
      await setNewActivity(newActivity);
    },
  });

  // Return null if user does not have 'Update' permissions
  if (!hasPermissions(ability, 'update', 'Activity')) {
    return null;
  }

  if (loading || wizardLoading) return <Loading />;
  if (error) return <Error />;

  const steps: WizardStepInterface[] = [
    { children: <ActivityDetails />, title: 'Activity Details' },
    { children: <StepDetails />, title: 'Build Activity' },
    {
      children: (
        <Overview
          entityType='Activity'
          title={newActivity.subject}
          description={newActivity.description}
          startDate={newActivity.startDate}
          dueDate={newActivity.dueDate}
          steps={newActivity.steps.filter((step) => !step.remove)}
          campaign={campaign?.name}
          stores={storeNames}
          assignedTo={assignedUserNames}
          hideHeader
        />
      ),
      title: 'Review',
      submitValidator: () => activityValidator(newActivity),
    },
  ];

  return (
    <Wizard
      title='Update Activity'
      steps={steps}
      onSubmit={() => handleSaveActivity({ state: 'Active', id: Number(activityId) })}
      submitting={saveActivityLoading}
      onSaveDraft={
        data?.activity.state === 'Draft'
          ? () => handleSaveActivity({ state: 'Draft', id: Number(activityId) })
          : undefined
      }
      onCancel={() => navigate('/activities')}
    />
  );
};

const UpdateActivityWizard = () => (
  <MediaGalleryPageContextProvider>
    <ActivitiesContextProvider>
      <CampaignsContextProvider>
        <StoresContextProvider>
          <ActivityWizardContextProvider>
            <UsersContextProvider>
              <UpdateActivityWizardComponent />
            </UsersContextProvider>
          </ActivityWizardContextProvider>
        </StoresContextProvider>
      </CampaignsContextProvider>
    </ActivitiesContextProvider>
  </MediaGalleryPageContextProvider>
);

export default UpdateActivityWizard;
